import React, { useState, useEffect } from 'react';
import { Upload, Button, message, Tooltip, Image, Card } from 'antd';
import { UploadOutlined, CopyOutlined, FileOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { uploadImage } from '@/api/user';
import LottiePreview from '@/components/LottiePreview';

const { Meta } = Card;
const LOCAL_STORAGE_KEY = 'uploadedImages';

const UploadPage: React.FC = () => {
  const [fileList, setFileList] = useState<any[]>([]);
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const [userEmail, setUserEmail] = useState<string | null>(null);

  useEffect(() => {
    const storedImages = JSON.parse(
      localStorage.getItem(LOCAL_STORAGE_KEY) || '[]'
    );
    setUploadedImages(storedImages);

    const email = localStorage.getItem('userEmail');
    setUserEmail(email);
  }, []);

  const mutation = useMutation({
    mutationFn: (formData: FormData) => uploadImage(formData),
    onSuccess: (data) => {
      const imageUrl = data.data;
      message.success('图片上传成功');

      if (!uploadedImages.includes(imageUrl)) {
        const updatedImages = [imageUrl, ...uploadedImages];
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(updatedImages));
        setUploadedImages(updatedImages);
      } else {
        message.warning('图片已存在，无需重复上传');
      }

      setFileList([]);
    },
    onError: () => {
      message.error('图片上传失败');
    },
  });

  const handleChange = (info: any) => {
    const isImage = info.file.type.startsWith('image/');
    const isSvga = info.file.name.toLowerCase().endsWith('.svga');
    const isLottie = info.file.name.toLowerCase().endsWith('.json') || info.file.name.toLowerCase().endsWith('.lottie');
    const isValidFile = isImage || isSvga || isLottie;
    
    if (!isValidFile) {
      message.error('只能上传图片类型文件、SVGA 或 Lottie 文件');
      return;
    }
    setFileList(info.fileList);
  };

  const handleUpload = () => {
    if (fileList.length === 0) {
      message.warning('请选择要上传的图片');
      return;
    }

    const formData = new FormData();
    fileList.forEach((file) => {
      formData.append('file', file.originFileObj);
    });

    mutation.mutate(formData);
  };

  const handleCopy = (url: string) => {
    navigator.clipboard
      .writeText(url)
      .then(() => {
        message.success('图片地址已复制到剪贴板');
      })
      .catch(() => {
        message.error('复制失败');
      });
  };

  // 判断文件格式
  const isSvgaFile = (url: string) => {
    return url.toLowerCase().endsWith('.svga');
  };

  const isLottieFile = (url: string) => {
    return url.toLowerCase().endsWith('.json') || url.toLowerCase().endsWith('.lottie');
  };

  // 渲染文件预览组件
  const renderFilePreview = (url: string, index: number) => {
    if (isSvgaFile(url)) {
      // SVGA 文件显示文件图标
      return (
        <div
          style={{
            width: '100%',
            height: '150px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#f5f5f5',
            borderRadius: '8px',
            border: '1px solid #d9d9d9',
          }}
        >
          <FileOutlined style={{ fontSize: '48px', color: '#1890ff', marginBottom: '8px' }} />
          <div style={{ fontSize: '12px', color: '#666', textAlign: 'center' }}>
            SVGA 文件
          </div>
        </div>
      );
    } else if (isLottieFile(url)) {
      // Lottie 文件显示动画预览
      return (
        <LottiePreview
          src={url}
          width="100%"
          height="150px"
          style={{
            background: '#f5f5f5',
            borderRadius: '8px',
            border: '1px solid #d9d9d9',
          }}
        />
      );
    } else {
      // 图片文件正常显示
      return (
        <Image 
          src={url} 
          alt="Uploaded" 
          style={styles.image}
          preview={{
            mask: <div style={{ fontSize: '12px' }}>Preview</div>
          }}
        />
      );
    }
  };

  return (
    <div style={styles.container}>
      <h1>图片上传</h1>
      {userEmail && <div style={styles.userInfo}>当前用户: {userEmail}</div>}
      <Upload
        listType="picture"
        fileList={fileList}
        onChange={handleChange}
        beforeUpload={() => false}
        accept="image/*,.svga,.json,.lottie,application/json,application/octet-stream"
      >
        <Button icon={<UploadOutlined />}>选择图片/SVGA/Lottie</Button>
      </Upload>
      <Button
        type="primary"
        onClick={handleUpload}
        style={styles.uploadButton}
        loading={mutation.isLoading}
      >
        上传图片
      </Button>

      <div style={styles.historyContainer}>
        <h3>上传历史</h3>
        <Image.PreviewGroup>
          <div style={styles.imageGrid}>
            {uploadedImages.map((imageUrl, index) => (
              <Card
                key={index}
                hoverable
                style={styles.card}
                cover={renderFilePreview(imageUrl, index)}
                actions={[
                  <Tooltip title="复制地址" key="copy">
                    <Button
                      icon={<CopyOutlined />}
                      size="small"
                      onClick={() => handleCopy(imageUrl)}
                    />
                  </Tooltip>,
                ]}
              >
                <Meta 
                  description={
                    isSvgaFile(imageUrl) 
                      ? `SVGA文件 ${index + 1}` 
                      : isLottieFile(imageUrl) 
                        ? `Lottie文件 ${index + 1}` 
                        : `图片 ${index + 1}`
                  } 
                />
              </Card>
            ))}
          </div>
        </Image.PreviewGroup>
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column' as 'column',
    alignItems: 'center',
    padding: '20px',
    backgroundColor: '#f5f5f5',
  },
  userInfo: {
    marginBottom: '10px',
    fontSize: '16px',
    color: '#333',
  },
  uploadButton: {
    marginTop: '20px',
    marginBottom: '20px',
  },
  historyContainer: {
    width: '100%',
    maxWidth: '800px',
  },
  imageGrid: {
    display: 'flex',
    flexWrap: 'wrap' as 'wrap',
    gap: '10px',
    justifyContent: 'center' as 'center',
  },
  card: {
    width: 150,
  },
  image: {
    width: '100%',
    height: '150px',
    objectFit: 'cover' as 'cover',
    borderRadius: '8px',
  },
};

export default UploadPage;
